import { useParams, Link, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VideoPlayer } from '../components/VideoPlayer';
import { MediaCarousel } from '../components/MediaCarousel';
import { Lightbox } from '../components/Lightbox';
import { PostButtons } from '../components/PostButtons';
import { RedesignSubmissions } from '../components/RedesignSubmissions';
import { RichTextView } from '../components/RichText';
import { Halftone } from '../components/Halftone';
import { Avatar } from '../components/Avatar';
import { Skeleton } from '../components/Skeleton';
import { WatchButton } from '../components/WatchButton';
import { Disclosure } from '../components/Disclosure';
import { useSession } from '../lib/session';
import {
  fetchPostBySlug,
  fetchComments,
  fetchViewerLikedPostIds,
  fetchViewerLikedCommentIds,
  fetchPollOptionsForPost,
  fetchAuctionForPost,
  fetchRecentBids,
  togglePostLike,
  toggleCommentLike,
  togglePollVote,
  toggleEventRsvp,
  placeBid,
  buyNow,
  fetchMyPaymentMethod,
  insertComment,
  nestComments,
  type CommentsCursor,
} from '../lib/queries';
import { AddCardModal } from '../components/AddCardModal';
import type { Post as PostType, Comment, PollOption, PostEvent, PostAuction, AuctionBid, Media } from '../lib/types';
import { buildIcs, downloadIcs } from '../lib/ics';
import { authorHandle, profilePath } from '../lib/display';
import './Post.css';

const RECENT_BIDS_LIMIT = 10;

export default function Post() {
  const { slug = '' } = useParams();
  const { profile } = useSession();
  const location = useLocation();

  const [post, setPost] = useState<PostType | null>(null);
  // Flat comments — nested into a tree at render time so paginated pages
  // merge cleanly (a reply on page 2 finds its parent from page 1).
  const [flatComments, setFlatComments] = useState<Comment[]>([]);
  const [commentsCursor, setCommentsCursor] = useState<CommentsCursor | null>(null);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [pollOptions, setPollOptions] = useState<PollOption[]>([]);
  const [eventState, setEventState] = useState<PostEvent | null>(null);
  const [auctionState, setAuctionState] = useState<PostAuction | null>(null);
  const [recentBids, setRecentBids] = useState<AuctionBid[]>([]);
  const [bidError, setBidError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Bidding-card gate (Sales Room). null = not yet known; true/false once
  // member_payment_methods is read. Only fetched for auction posts.
  const [cardOnFile, setCardOnFile] = useState<boolean | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const p = await fetchPostBySlug(slug, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (!p) { setNotFound(true); return; }
        setPost(p);
        setLikeCount(p.like_count ?? 0);
        setCommentCount(p.comment_count ?? 0);
        // fetchPostBySlug already attaches event + auction + viewer state.
        // Hydrate local state directly from those — mirrors how polls used to
        // be re-fetched but skipping the round trip now that the post fetch
        // bundles it for us.
        setEventState(p.event ?? null);
        setAuctionState(p.auction ?? null);

        const page = await fetchComments(p.id, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;

        if (profile) {
          const [postLiked, commentLiked, polls, bids] = await Promise.all([
            fetchViewerLikedPostIds([p.id], profile.id, ctrl.signal),
            fetchViewerLikedCommentIds(page.flat.map((c) => c.id), profile.id, ctrl.signal),
            fetchPollOptionsForPost(p.id, profile.id, ctrl.signal),
            p.auction ? fetchRecentBids(p.id, RECENT_BIDS_LIMIT, ctrl.signal) : Promise.resolve<AuctionBid[]>([]),
          ]);
          if (ctrl.signal.aborted) return;
          setLiked(postLiked.has(p.id));
          setFlatComments(page.flat.map((c) => ({ ...c, viewer_liked: commentLiked.has(c.id) })));
          setPollOptions(polls);
          setRecentBids(bids);
        } else {
          const [polls, bids] = await Promise.all([
            fetchPollOptionsForPost(p.id, null, ctrl.signal),
            p.auction ? fetchRecentBids(p.id, RECENT_BIDS_LIMIT, ctrl.signal) : Promise.resolve<AuctionBid[]>([]),
          ]);
          if (ctrl.signal.aborted) return;
          setFlatComments(page.flat);
          setPollOptions(polls);
          setRecentBids(bids);
        }
        setCommentsCursor(page.nextCursor);
        setHasMoreComments(page.nextCursor !== null);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[post] fetch failed', err);
        setNotFound(true);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [slug, profile?.id]);

  // Read the viewer's bidding-card state, but only for auction posts and only
  // when signed in. Drives the "Add a card to bid" gate in AuctionBlock; the
  // place_bid RPC enforces it for real regardless.
  const refreshCard = useCallback(() => {
    fetchMyPaymentMethod()
      .then((pm) => setCardOnFile(!!pm?.has_card_on_file))
      .catch(() => setCardOnFile(false));
  }, []);

  useEffect(() => {
    if (!profile || !auctionState) { setCardOnFile(null); return; }
    let cancelled = false;
    fetchMyPaymentMethod()
      .then((pm) => { if (!cancelled) setCardOnFile(!!pm?.has_card_on_file); })
      .catch(() => { if (!cancelled) setCardOnFile(false); });
    return () => { cancelled = true; };
  }, [profile?.id, auctionState?.post_id]);

  const loadMoreComments = useCallback(async () => {
    if (loadingMoreComments || !hasMoreComments || !commentsCursor || !post) return;
    setLoadingMoreComments(true);
    const ctrl = new AbortController();
    try {
      const page = await fetchComments(post.id, { cursor: commentsCursor, signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      let pageItems = page.flat;
      if (profile && pageItems.length > 0) {
        const liked = await fetchViewerLikedCommentIds(
          pageItems.map((c) => c.id),
          profile.id,
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        pageItems = pageItems.map((c) => ({ ...c, viewer_liked: liked.has(c.id) }));
      }
      setFlatComments((prev) => [...prev, ...pageItems]);
      setCommentsCursor(page.nextCursor);
      setHasMoreComments(page.nextCursor !== null);
    } catch (err: any) {
      if (ctrl.signal.aborted || err?.name === 'AbortError') return;
      console.error('[post] loadMoreComments failed', err);
    } finally {
      setLoadingMoreComments(false);
    }
  }, [commentsCursor, hasMoreComments, loadingMoreComments, post, profile]);

  // Build the nested tree from the merged flat list. Re-runs on every
  // pagination append + every optimistic like/reply mutation.
  const comments = useMemo(() => nestComments(flatComments), [flatComments]);

  // Deep-link: if URL has #comment=<id>, scroll it into view + highlight for 6s.
  const [permalinkId, setPermalinkId] = useState<string | null>(null);
  useEffect(() => {
    const hash = location.hash || '';
    const match = /#comment=([A-Za-z0-9\-]+)/.exec(hash);
    if (!match) return;
    if (loading) return;
    const id = match[1];
    const el = document.getElementById(`comment-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPermalinkId(id);
    const t = setTimeout(() => setPermalinkId(null), 6000);
    return () => clearTimeout(t);
  }, [loading, location.hash]);

  // Live-auction polling. Mirrors the plan: every 5s normally, every 2s in
  // the final minute. The effect is scoped to live auctions only — when
  // auctionState is null or not 'live', the early `return` leaves no timer
  // running, so non-auction posts pay zero polling cost.
  //
  // ends_at is in the dep array so that anti-snipe extensions (which push
  // ends_at forward inside place_bid) reset the interval — otherwise we'd
  // stay on the 2s fast-poll forever once we entered it.
  useEffect(() => {
    if (!post?.id || !auctionState || auctionState.state !== 'live') return;
    const endsAtMs = new Date(auctionState.ends_at).getTime();
    const msLeft = endsAtMs - Date.now();
    const interval = msLeft < 60_000 ? 2000 : 5000;
    const viewerId = profile?.id ?? null;
    const postId = post.id;
    const tick = async () => {
      try {
        const [next, bids] = await Promise.all([
          fetchAuctionForPost(postId, viewerId),
          fetchRecentBids(postId, RECENT_BIDS_LIMIT),
        ]);
        if (next) {
          setAuctionState((cur) => {
            // Carry forward viewer_watching from the current local state —
            // fetchAuctionForPost doesn't re-resolve it on this batch path.
            next.viewer_watching = cur?.viewer_watching ?? next.viewer_watching;
            return next;
          });
        }
        setRecentBids(bids);
      } catch (err) {
        console.error('[auction-poll] failed', err);
      }
    };
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
  }, [post?.id, auctionState?.state, auctionState?.ends_at, profile?.id]);

  async function handlePollToggle(optionId: string) {
    if (!profile || !post) return;
    const target = pollOptions.find((o) => o.id === optionId);
    if (!target) return;
    const wasVoted = target.viewer_voted;
    // Optimistic: flip viewer_voted and adjust vote_count.
    setPollOptions((prev) =>
      prev.map((o) =>
        o.id === optionId
          ? { ...o, viewer_voted: !wasVoted, vote_count: o.vote_count + (wasVoted ? -1 : 1) }
          : o,
      ),
    );
    try {
      await togglePollVote(post.id, optionId, profile.id, wasVoted);
    } catch (err) {
      console.error('[poll-vote] failed', err);
      setPollOptions((prev) =>
        prev.map((o) =>
          o.id === optionId
            ? { ...o, viewer_voted: wasVoted, vote_count: o.vote_count + (wasVoted ? 1 : -1) }
            : o,
        ),
      );
    }
  }

  async function handleRsvpToggle() {
    if (!profile || !post || !eventState) return;
    const wasGoing = !!eventState.viewer_going;
    const prev = eventState;
    setEventState({
      ...eventState,
      viewer_going: !wasGoing,
      going_count: eventState.going_count + (wasGoing ? -1 : 1),
    });
    try {
      await toggleEventRsvp(post.id, profile.id, wasGoing);
    } catch (err) {
      console.error('[event-rsvp] failed', err);
      setEventState(prev);
    }
  }

  function handleAddToCalendar() {
    if (!post || !eventState) return;
    const ics = buildIcs({
      title: post.title,
      startUtc: eventState.starts_at,
      endUtc: eventState.ends_at,
      description: post.dek ?? post.body_md ?? undefined,
      url: eventState.location_kind === 'online' ? (eventState.location_url ?? undefined) : undefined,
      location: eventState.location_kind === 'in_person'
        ? (eventState.location_address ?? undefined)
        : (eventState.location_url ?? undefined),
      uid: post.slug,
    });
    downloadIcs(`${post.slug}.ics`, ics);
  }

  async function handlePlaceBid(bidDollars: number) {
    if (!profile || !post || !auctionState) return;
    setBidError(null);
    const prev = auctionState;
    // Direct bidding: the bid is exactly what they typed, so we can optimistically
    // show it as the new top + flag them as high bidder. AuctionBlock keys
    // hasBids (Current-bid label, Buy Now removal, sticky-bar amount) on
    // bid_count, so bump the counts too — otherwise the FIRST bid renders a
    // stale "Starting bid" block with Buy Now still offered. The refetch right
    // after reconciles against the canonical state (e.g. a concurrent higher bid).
    const viewerAlreadyBid =
      auctionState.viewer_is_high_bidder ||
      recentBids.some((b) => b.bidder_id === profile.id);
    setAuctionState({
      ...auctionState,
      current_high_amount: bidDollars * 100,
      current_high_bidder_id: profile.id,
      viewer_is_high_bidder: true,
      bid_count: auctionState.bid_count + 1,
      bidder_count: auctionState.bidder_count + (viewerAlreadyBid ? 0 : 1),
    });
    try {
      await placeBid(post.id, bidDollars * 100);
      const [next, bids] = await Promise.all([
        fetchAuctionForPost(post.id, profile.id),
        fetchRecentBids(post.id, RECENT_BIDS_LIMIT),
      ]);
      if (next) {
        // Preserve watch state from the local snapshot (fetchAuctionForPost
        // here doesn't re-attach viewer_watching because we don't pass the
        // batch through fetchWatchedPostIds).
        next.viewer_watching = prev.viewer_watching;
        setAuctionState(next);
      }
      setRecentBids(bids);
    } catch (err: any) {
      console.error('[place-bid] failed', err);
      setAuctionState(prev);
      setBidError(mapBidError(err));
      // Card was removed/expired since we last checked — re-show the gate.
      if (String(err?.message ?? '').includes('no_card_on_file')) setCardOnFile(false);
    }
  }

  // Buy Now (first-bid rule): server inserts a bid at buy_now_price and ends
  // the lot. No optimistic update — the refetch flips the block to its ended
  // state, where bought_now_at + viewer_is_high_bidder drive the "lot is
  // yours" line so it survives reload.
  async function handleBuyNow() {
    if (!profile || !post || !auctionState) return;
    setBidError(null);
    const prev = auctionState;
    try {
      await buyNow(post.id);
      const [next, bids] = await Promise.all([
        fetchAuctionForPost(post.id, profile.id),
        fetchRecentBids(post.id, RECENT_BIDS_LIMIT),
      ]);
      if (next) {
        // Preserve watch state from the local snapshot (same caveat as
        // handlePlaceBid — this fetch path doesn't re-attach viewer_watching).
        next.viewer_watching = prev.viewer_watching;
        setAuctionState(next);
      }
      setRecentBids(bids);
    } catch (err: any) {
      console.error('[buy-now] failed', err);
      setBidError(mapBidError(err));
      // Card was removed/expired since we last checked — re-show the gate.
      if (String(err?.message ?? '').includes('no_card_on_file')) setCardOnFile(false);
    }
  }

  async function handlePostLike() {
    if (!profile || !post) return;
    const wasLiked = liked;
    // Optimistic
    setLiked(!wasLiked);
    setLikeCount((n) => n + (wasLiked ? -1 : 1));
    try {
      const nextLiked = await togglePostLike(post.id, profile.id, wasLiked);
      setLiked(nextLiked);
    } catch (err) {
      console.error('[post-like] failed', err);
      setLiked(wasLiked);
      setLikeCount((n) => n + (wasLiked ? 1 : -1));
    }
  }

  async function handleAddComment(body: string, parentId: string | null) {
    if (!profile || !post) return;
    const created = await insertComment({
      postId: post.id,
      authorId: profile.id,
      body,
      parentId,
    });
    // Append to flat list. nestComments() will re-thread it under the
    // right parent on the next render. parent_id is preserved by
    // insertComment so threading is automatic.
    setFlatComments((prev) => [...prev, { ...created, parent_id: parentId ?? null }]);
    setCommentCount((n) => n + 1);
  }

  async function handleCommentLike(commentId: string, currentlyLiked: boolean) {
    if (!profile) return;
    // Mutate flat list. The render-time nestComments() picks the new
    // viewer_liked + like_count up automatically.
    const mutate = (mutator: (c: Comment) => Comment) =>
      setFlatComments((prev) => prev.map((c) => (c.id === commentId ? mutator(c) : c)));

    mutate((c) => ({
      ...c,
      viewer_liked: !currentlyLiked,
      like_count: c.like_count + (currentlyLiked ? -1 : 1),
    }));
    try {
      const next = await toggleCommentLike(commentId, profile.id, currentlyLiked);
      mutate((c) => ({ ...c, viewer_liked: next }));
    } catch (err) {
      console.error('[comment-like] failed', err);
      mutate((c) => ({
        ...c,
        viewer_liked: currentlyLiked,
        like_count: c.like_count + (currentlyLiked ? 1 : -1),
      }));
    }
  }

  if (loading) {
    return (
      <article className="post">
        <div className="container container--md" aria-busy="true" aria-label="Loading post">
          <div className="post__skel-back"><Skeleton variant="line" width={64} height={10} /></div>
          <div className="post__skel-eyebrow"><Skeleton variant="line" width={120} height={10} /></div>
          <div className="post__skel-title">
            <Skeleton variant="text" width="90%" height={28} />
            <Skeleton variant="text" width="72%" height={28} />
            <Skeleton variant="text" width="48%" height={28} />
          </div>
          <div className="post__skel-cover"><Skeleton variant="card" aspectRatio={16 / 9} /></div>
          <div className="post__skel-body">
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="94%" />
            <Skeleton variant="text" width="98%" />
            <Skeleton variant="text" width="88%" />
            <Skeleton variant="text" width="92%" />
            <Skeleton variant="text" width="60%" />
          </div>
          <div className="post__skel-bar">
            <Skeleton variant="line" width={100} height={32} />
            <Skeleton variant="line" width={80}  height={32} />
          </div>
          <div className="post__skel-body">
            <Skeleton variant="text" width="30%" height={20} />
          </div>
        </div>
      </article>
    );
  }
  if (notFound || !post) {
    return (
      <div className="container post">
        <p className="kicker">Not found</p>
        <h1>This post isn't here.</h1>
        <p>It may have been removed, or the link might be off.</p>
        <Link to="/">← Back to the feed</Link>
      </div>
    );
  }

  const drawer = post.drawers?.[0];
  // Video poster: the per-video thumbnail (media.thumbnail_url, set in the
  // wizard's media list) wins; the post-level feed thumbnail is the fallback.
  const cover = post.media?.thumbnail_url ?? post.thumbnail_url ?? undefined;
  const aspect = post.media?.aspect_ratio ?? 16 / 9;
  // For videos: pass mediaId to VideoPlayer — it fetches a signed/managed
  // playback URL. Legacy demo posts store a raw HLS URL in provider_id;
  // VideoPlayer's `src` prop is kept as a backward-compat fallback.
  const videoFallbackSrc =
    post.media?.kind === 'video' && post.media.provider_id?.startsWith('http')
      ? post.media.provider_id
      : null;
  const videoMediaId = post.media?.kind === 'video' ? post.media.id : null;

  const isTextPost = post.post_type === 'text';
  const isImagePost = post.post_type === 'image';
  const isVideoPost = post.post_type === 'video';

  // Ordered, mixed image+video items (from post_media; the query layer falls
  // back to legacy media/image_urls for un-backfilled posts). A lone video
  // keeps the dedicated player (preserves the legacy HLS `src` fallback);
  // anything else with media renders the swipe carousel + lightbox.
  const mediaItems: Media[] = post.media_items ?? [];
  const loneVideo = mediaItems.length === 1 && mediaItems[0].kind === 'video';
  const useCarousel = mediaItems.length > 0 && !loneVideo;
  const isRedesign = post.format === 'redesign';
  const traceUrl = post.resource_url ?? drawer?.default_resource_url ?? null;
  const isAuction = !!auctionState;

  // The lot gallery (left column on auction posts) — same hero/video/carousel
  // logic as the standard post body, but with the thumbnail filmstrip enabled.
  const galleryNode = (
    <>
      {mediaItems.length === 0 && !videoMediaId && !videoFallbackSrc && (
        <div className="post__hero" aria-hidden="true">
          <span className="post__hero-art"><Halftone type="bloom" /></span>
          {drawer && <span className="post__hero-tag">{drawer.name}</span>}
        </div>
      )}
      {!useCarousel && (videoMediaId || videoFallbackSrc) && (
        <div className="post__video">
          <VideoPlayer mediaId={videoMediaId} src={videoFallbackSrc} poster={cover} aspect={aspect} />
        </div>
      )}
      {useCarousel && <PostMedia items={mediaItems} aspect={aspect} thumbs />}
    </>
  );

  return (
    <article className={`post ${isTextPost ? 'post--text' : ''} ${isImagePost ? 'post--image' : ''} ${isAuction ? 'post--auction' : ''}`}>
      <div className={`container ${isAuction ? '' : 'container--md'}`}>
        {/* Every post: back (left) · closet name (centred) on one line. Lots
            come from the Saleroom grid, so their back leads there, not the feed. */}
        <div className={`post__topbar ${isAuction ? 'post__topbar--auction' : ''}`}>
          <Link to={isAuction ? '/sales-room' : '/'} className="post__back">← Back</Link>
          {drawer && <p className="post__drawer kicker">{drawer.name}</p>}
        </div>

        {auctionState ? (
          <AuctionDetail post={post} auction={auctionState} gallery={galleryNode} />
        ) : (
          <>
            {!post.hide_title && <h1 className="post__title">{post.title}</h1>}

            {mediaItems.length === 0 && !videoMediaId && !videoFallbackSrc && (
              <div className="post__hero" aria-hidden="true">
                <span className="post__hero-art"><Halftone type="bloom" /></span>
                {drawer && <span className="post__hero-tag">{drawer.name}</span>}
              </div>
            )}

            {!useCarousel && (videoMediaId || videoFallbackSrc) && (
              <div className="post__video">
                <VideoPlayer
                  mediaId={videoMediaId}
                  src={videoFallbackSrc}
                  poster={cover}
                  aspect={aspect}
                />
              </div>
            )}

            {useCarousel && <PostMedia items={mediaItems} aspect={aspect} />}

            {post.body_md && <RichTextView html={post.body_md} className="post__body" />}

            {isRedesign && traceUrl && (
              <div className="post__resource">
                <a
                  className="btn btn--primary btn--sm"
                  href={traceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  ↓ Download trace resource
                </a>
                <p className="post__resource-hint">Print it, sketch your redesign on top, then submit below.</p>
              </div>
            )}

            {post.buttons && post.buttons.length > 0 && <PostButtons buttons={post.buttons} />}

            {isRedesign && <RedesignSubmissions postId={post.id} />}
          </>
        )}

        {auctionState && (
          <AuctionBlock
            auction={auctionState}
            recentBids={recentBids}
            viewerId={profile?.id ?? null}
            canBid={!!profile}
            isAuthor={!!profile && profile.id === post.author_id}
            cardOnFile={cardOnFile}
            bidError={bidError}
            onPlaceBid={handlePlaceBid}
            onBuyNow={handleBuyNow}
            onAddCard={() => setShowAddCard(true)}
            onDismissBidError={() => setBidError(null)}
          />
        )}

        {showAddCard && (
          <AddCardModal
            onClose={() => setShowAddCard(false)}
            onLinked={refreshCard}
          />
        )}

        {eventState && (
          <EventBlock
            event={eventState}
            canRsvp={!!profile}
            onRsvpToggle={handleRsvpToggle}
            onAddToCalendar={handleAddToCalendar}
          />
        )}

        {pollOptions.length > 0 && (
          <PollBlock
            options={pollOptions}
            canVote={!!profile}
            onToggle={handlePollToggle}
          />
        )}

        <div className="post__bar">
          <LikeButton n={likeCount} liked={liked} onToggle={handlePostLike} />
          <ShareButton slug={post.slug} />
          {auctionState && (
            <WatchButton
              postId={auctionState.post_id}
              initialWatching={!!auctionState.viewer_watching}
              initialCount={auctionState.watch_count}
              variant="compact"
              canWatch={auctionState.state !== 'ended'}
            />
          )}
        </div>


        <section className="post__comments" aria-label="Comments">
          <h2>Comments · {commentCount}</h2>
          {comments.length > 0 && (
            <>
              <ol className="comments">
                {comments.map((c) => (
                  <CommentItem
                    key={c.id}
                    comment={c}
                    postSlug={post.slug}
                    highlightId={permalinkId}
                    onLike={handleCommentLike}
                    onReply={(body) => handleAddComment(body, c.id)}
                  />
                ))}
              </ol>
              {hasMoreComments && (
                <div className="post__comments-more">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={loadMoreComments}
                    disabled={loadingMoreComments}
                  >
                    {loadingMoreComments ? 'Loading…' : 'Load more comments'}
                  </button>
                </div>
              )}
            </>
          )}
          {profile
            ? <CommentComposer onSubmit={(body) => handleAddComment(body, null)} />
            : <p className="comment-composer__signin">Sign in to join the conversation.</p>}
        </section>
      </div>
    </article>
  );
}

function CommentItem({
  comment,
  postSlug,
  highlightId,
  onLike,
  onReply,
}: {
  comment: Comment;
  postSlug: string;
  highlightId: string | null;
  onLike: (id: string, currentlyLiked: boolean) => Promise<void>;
  onReply: (body: string) => Promise<void>;
}) {
  const [showReply, setShowReply] = useState(false);
  // Replies collapse by default (Instagram-style); a toggle reveals them.
  const [showReplies, setShowReplies] = useState(false);
  const authorName = authorHandle(comment.author);
  const founding = comment.author?.is_founding_member ?? false;
  const authorTo = profilePath(comment.author);
  const highlighted = highlightId === comment.id;
  const replyCount = comment.replies?.length ?? 0;
  const ref = useRef<HTMLLIElement>(null);

  // Auto-expand if a permalinked comment lives inside this thread, so the
  // deep-link scroll/highlight can find it.
  useEffect(() => {
    if (highlightId && comment.replies?.some((r) => r.id === highlightId)) {
      setShowReplies(true);
    }
  }, [highlightId, comment.replies]);

  return (
    <li
      ref={ref}
      id={`comment-${comment.id}`}
      className={`comment ${highlighted ? 'comment--highlighted' : ''}`}
    >
      {authorTo ? (
        <Link to={authorTo} className="comment__avatar-link" aria-label={`View @${authorName}`}>
          <Avatar name={authorName} src={comment.author?.avatar_url} size="sm" founding={founding} />
        </Link>
      ) : (
        <Avatar name={authorName} src={comment.author?.avatar_url} size="sm" founding={founding} />
      )}
      <div className="comment__body">
        <p className="comment__head">
          {authorTo ? (
            <Link to={authorTo} className="comment__author comment__author--link">@{authorName}</Link>
          ) : (
            <span className="comment__author">@{authorName}</span>
          )}
          {founding && <span className="comment__founding">Founding</span>}
          <span className="comment__time">{formatRelative(comment.created_at)}</span>
        </p>
        <p className="comment__text">{comment.body}</p>
        <p className="comment__meta">
          <button
            type="button"
            className={`comment__action ${comment.viewer_liked ? 'comment__action--on' : ''}`}
            onClick={() => onLike(comment.id, !!comment.viewer_liked)}
          >
            {comment.viewer_liked ? '♥' : '♡'} {comment.like_count}
          </button>
          {comment.parent_id === null && (
            <button
              type="button"
              className="comment__action"
              onClick={() => setShowReply((v) => !v)}
            >
              Reply
            </button>
          )}
          <ShareCommentButton postSlug={postSlug} commentId={comment.id} />
        </p>

        {showReply && (
          <CommentComposer
            compact
            onSubmit={async (body) => {
              await onReply(body);
              setShowReply(false);
            }}
          />
        )}

        {replyCount > 0 && (
          <button
            type="button"
            className="comment__replies-toggle"
            onClick={() => setShowReplies((v) => !v)}
            aria-expanded={showReplies}
          >
            {showReplies ? 'Hide replies' : `Replies (${replyCount})`}
          </button>
        )}

        {showReplies && replyCount > 0 && (
          <ol className="comments comments--nested">
            {comment.replies!.map((r) => (
              <CommentItem
                key={r.id}
                comment={r}
                postSlug={postSlug}
                highlightId={highlightId}
                onLike={onLike}
                onReply={async () => {}}
              />
            ))}
          </ol>
        )}
      </div>
    </li>
  );
}

function CommentComposer({
  compact,
  onSubmit,
}: {
  compact?: boolean;
  onSubmit: (body: string) => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(value.trim());
      setValue('');
    } catch (err) {
      // Don't clear the textarea — the user shouldn't lose what they wrote.
      console.error('[comment] submit failed', err);
      setError("That comment didn't post. Try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className={`comment-composer ${compact ? 'comment-composer--compact' : ''}`} onSubmit={submit}>
      <textarea
        placeholder={compact ? 'Reply…' : 'Leave a comment…'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={4000}
      />
      <button type="submit" className="btn btn--primary btn--sm" disabled={!value.trim() || busy}>
        {busy ? 'Posting…' : 'Post'}
      </button>
      {error && <p className="comment-composer__error" role="alert">{error}</p>}
    </form>
  );
}

function PollBlock({
  options,
  canVote,
  onToggle,
}: {
  options: PollOption[];
  canVote: boolean;
  onToggle: (optionId: string) => void;
}) {
  const totalVotes = options.reduce((n, o) => n + o.vote_count, 0);
  // Leader-relative bar fill: the winning option fills fully, others
  // scale against it. Falls back to 1 to avoid divide-by-zero before any
  // votes are cast.
  const leader = Math.max(1, ...options.map((o) => o.vote_count));
  return (
    <div className="poll" role="group" aria-label="Poll">
      <ul className="poll__list">
        {options.map((o) => {
          const pct = totalVotes === 0 ? 0 : Math.round((o.vote_count / totalVotes) * 100);
          const fill = Math.round((o.vote_count / leader) * 100);
          return (
            <li key={o.id} className={`poll__item ${o.viewer_voted ? 'poll__item--on' : ''}`}>
              <button
                type="button"
                className="poll__btn"
                onClick={() => onToggle(o.id)}
                disabled={!canVote}
                aria-pressed={o.viewer_voted}
              >
                <span className="poll__bar" aria-hidden="true" style={{ width: `${fill}%` }} />
                <span className="poll__check" aria-hidden="true">{o.viewer_voted ? '✓' : ''}</span>
                <span className="poll__label">{o.label}</span>
                <span className="poll__count">{o.vote_count} · {pct}%</span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="poll__meta">
        {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'} · pick any that apply
        {!canVote && ' · sign in to vote'}
      </p>
    </div>
  );
}

function EventBlock({
  event,
  canRsvp,
  onRsvpToggle,
  onAddToCalendar,
}: {
  event: PostEvent;
  canRsvp: boolean;
  onRsvpToggle: () => void;
  onAddToCalendar: () => void;
}) {
  const now = Date.now();
  const startMs = new Date(event.starts_at).getTime();
  const endMs = event.ends_at ? new Date(event.ends_at).getTime() : null;
  // An event is "ended" when either: it has an end and that end has passed,
  // OR there's no end and the start was more than an hour ago. The 1h fallback
  // matches the ICS default-end behaviour.
  const eventEnded =
    endMs !== null ? endMs < now : startMs + 60 * 60 * 1000 < now;

  const dateLabel = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(event.starts_at));
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(event.starts_at));

  const isOnline = event.location_kind === 'online';
  const going = !!event.viewer_going;

  return (
    <section className="event" aria-label="Event">
      <p className="event__eyebrow">Event</p>
      <p className="event__when">
        {dateLabel} · {timeLabel}
      </p>
      <p className="event__where">
        {isOnline ? (
          <>
            <span aria-hidden="true">🎥</span> Online
            {event.location_url && (
              <>
                {' · '}
                <a
                  href={event.location_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event__join"
                >
                  Join link
                </a>
              </>
            )}
          </>
        ) : (
          <>
            <span aria-hidden="true">📍</span> {event.location_address ?? 'In person'}
          </>
        )}
      </p>

      {eventEnded ? (
        <p className="event__ended">This event has ended.</p>
      ) : (
        <div className="event__actions">
          <button
            type="button"
            className={`event__rsvp ${going ? 'event__rsvp--on' : ''}`}
            onClick={onRsvpToggle}
            disabled={!canRsvp}
            aria-pressed={going}
          >
            <span aria-hidden="true">{going ? '♥' : '♡'}</span>
            {going ? "You're going" : "I'm going"}
          </button>
          <span className="event__count">
            {event.going_count} {event.going_count === 1 ? 'going' : 'going'}
          </span>
          <button
            type="button"
            className="event__cal"
            onClick={onAddToCalendar}
          >
            Add to my calendar
          </button>
        </div>
      )}
    </section>
  );
}

function AuctionBlock({
  auction,
  recentBids,
  viewerId,
  canBid,
  isAuthor,
  cardOnFile,
  bidError,
  onPlaceBid,
  onBuyNow,
  onAddCard,
  onDismissBidError,
}: {
  auction: PostAuction;
  recentBids: AuctionBid[];
  viewerId: string | null;
  canBid: boolean;
  isAuthor: boolean;
  /** Viewer's bidding-card state: null = still loading, true/false once known. */
  cardOnFile: boolean | null;
  bidError: string | null;
  onPlaceBid: (maxBidDollars: number) => void;
  onBuyNow: () => Promise<void>;
  onAddCard: () => void;
  onDismissBidError: () => void;
}) {
  // Direct bidding: the first bid must meet the lot's starting bid;
  // every later bid must beat the current top by at least $1.
  const minNextDollars = auction.current_high_amount > 0
    ? Math.floor(auction.current_high_amount / 100) + 1
    : Math.max(1, Math.ceil(auction.min_bid / 100));
  // maxBid holds bare digits ("6000"); the input displays it formatted —
  // commas while typing ("6,000"), commas + cents once blurred ("6,000.00").
  const [maxBid, setMaxBid] = useState<string>('');
  const [bidFocused, setBidFocused] = useState(false);
  const bidDisplay = maxBid
    ? Number(maxBid).toLocaleString('en-US') + (bidFocused ? '' : '.00')
    : '';
  // Reset the input whenever the live floor moves (someone else bid).
  useEffect(() => { setMaxBid(''); }, [auction.current_high_amount, auction.ends_at]);

  const isLive = auction.state === 'live';
  const isEnded = auction.state === 'ended';
  const hasBids = auction.bid_count > 0;
  const postage = auction.shipping_cost;   // cents; null = unspecified, 0 = free

  // Live receipt under the input: the bid figure floors at the minimum so
  // the line always shows a placeable total, even before they type.
  const typedBid = parseInt(maxBid, 10);
  const receiptBidDollars =
    Number.isNaN(typedBid) || typedBid < minNextDollars ? minNextDollars : typedBid;

  function submitBid(e: React.FormEvent) {
    e.preventDefault();
    const n = parseInt(maxBid, 10);
    if (Number.isNaN(n) || n < minNextDollars) return;
    onPlaceBid(n);
  }

  // Buy Now follows the first-bid rule: only offered while the lot is live
  // with zero bids, and only to members who could actually place a bid.
  const buyNowAvailable =
    auction.buy_now_price != null && !hasBids && isLive &&
    canBid && !isAuthor && cardOnFile === true;
  // Inline confirm step (no modal). Collapses if eligibility vanishes
  // mid-confirm (someone places the first bid while they're deciding).
  const [confirmingBuyNow, setConfirmingBuyNow] = useState(false);
  const [buyNowBusy, setBuyNowBusy] = useState(false);
  useEffect(() => { if (!buyNowAvailable) setConfirmingBuyNow(false); }, [buyNowAvailable]);

  async function handleConfirmBuyNow() {
    setBuyNowBusy(true);
    try {
      await onBuyNow();   // errors surface via bidError in the parent
    } finally {
      setBuyNowBusy(false);
      setConfirmingBuyNow(false);
    }
  }

  // Mobile sticky bid bar: only while live, and only once the bid card has
  // scrolled out of view (IntersectionObserver on the stack).
  const stackRef = useRef<HTMLDivElement>(null);
  const bidInputRef = useRef<HTMLInputElement>(null);
  const [stackInView, setStackInView] = useState(true);
  useEffect(() => {
    if (!isLive) return;
    const el = stackRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setStackInView(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [isLive]);
  const jumpToBidCard = useCallback(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    stackRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    bidInputRef.current?.focus({ preventScroll: true });
  }, []);

  // Viewer-state messaging. The big high-bidder/outbid status lives in the
  // top band's right-hand space (bidderStatus); the form band keeps a quiet
  // prompt line. "Outbid" detection looks for the viewer in recentBids when
  // they're not the current high bidder. recentBids is capped at the last 10
  // so a long bid war could push the viewer's row out of the window; that's
  // a known best-effort limitation.
  const viewerHasBid = viewerId != null && recentBids.some((b) => b.bidder_id === viewerId);
  const bidderStatus: { kind: 'high' | 'outbid'; text: string } | null =
    isLive && canBid && !isAuthor
      ? auction.viewer_is_high_bidder
        ? { kind: 'high', text: "You're the highest bidder!" }
        : viewerHasBid
          ? { kind: 'outbid', text: "You've been outbid." }
          : null
      : null;
  let viewerMessage: string | null = null;
  if (isLive) {
    if (isAuthor) {
      viewerMessage = 'This is your lot, you can\'t bid on it.';
    } else if (!canBid) {
      viewerMessage = 'Members can bid.';
    } else if (cardOnFile === true) {
      viewerMessage = 'Place your bid below';
    }
  }

  // One card, two bands (price + countdown chip → form), separated from the
  // "soft" item details above (AuctionDetail). Once the lot ends the card
  // collapses to the frosted SOLD / UNSOLD stamp; a Buy Now winner also gets
  // a confirmation line, driven off bought_now_at + viewer_is_high_bidder so
  // it survives reload.
  const stampLabel = hasBids ? 'SOLD' : 'UNSOLD';
  const boughtByViewer = isEnded && !!auction.bought_now_at && !!auction.viewer_is_high_bidder;
  const showFormBand =
    !!viewerMessage || !!bidError || (isLive && canBid && !isAuthor && cardOnFile !== null);

  return (
    <>
    <div className="bidstack" ref={stackRef}>
    <section className={`bidblock ${isEnded ? 'bidblock--ended' : ''}`} aria-label="Bidding">
      {isEnded && <div className="bidblock__stamp">{stampLabel}</div>}
      {isEnded && boughtByViewer && (
        <p className="bidblock__yours">
          This lot is yours — the team will confirm your order and charge your card on file.
        </p>
      )}
      {!isEnded && (
      <>
      <div className="bidblock__top">
        <div className="bidblock__top-main">
          <span className="bidblock__label">{hasBids ? 'Current bid' : 'Starting bid'}</span>
          {/* Shared shrink-wrap grid: the amount box and the countdown row
              stretch to the same width so their edges align. */}
          <div className="bidblock__stack">
            <div className="bidblock__amount-row">
              <span className="bidblock__amount">
                {dollars(hasBids ? auction.current_high_amount : auction.min_bid)}
              </span>
              {postage !== null && (
                <span className="bidblock__postchip">
                  {postage === 0
                    ? 'Free postage'
                    : <>+ <strong>{dollars(postage)}</strong> postage</>}
                </span>
              )}
              {/* Mobile-only twin of the band status: rides this row so the
                  message sits NEXT TO the current-bid box (the desktop band
                  status hides on phones, so only one is ever visible). */}
              {bidderStatus && (
                <p className={`bidblock__status-inline bidblock__status-inline--${bidderStatus.kind}`}>
                  {bidderStatus.text}
                </p>
              )}
            </div>
            <AuctionCountdown auction={auction} />
          </div>
          {/* Bids are anonymous — we surface the count, never who holds the top bid. */}
          <p className="bidblock__meta">
            <strong>{auction.bid_count}</strong> {auction.bid_count === 1 ? 'bid' : 'bids'}
            <span className="bidblock__meta-dot" aria-hidden="true">·</span>
            <strong>{auction.watch_count}</strong> watching
          </p>
        </div>
        {/* High-bidder / outbid status fills the band's right-hand space.
            The whole high-bidder line flows as ONE phrase in ONE font, all
            in the chosen accent (team request). */}
        {bidderStatus && (
          <p className={`bidblock__status bidblock__status--${bidderStatus.kind}`}>
            {bidderStatus.text}
          </p>
        )}
      </div>

      {showFormBand && (
      <div className="bidblock__formband">
      {viewerMessage && (
        <p className="bidblock__viewer">{viewerMessage}</p>
      )}

      {isLive && canBid && !isAuthor && cardOnFile === false && (
        <div className="bidblock__cardgate">
          <button type="button" className="bidblock__addcard" onClick={onAddCard}>
            Register to bid
          </button>
          <p className="bidblock__help">
            A quick one-time setup — your contact details and a card on file. You're only charged if you win.
          </p>
        </div>
      )}

      {isLive && canBid && !isAuthor && cardOnFile === true && (
        <>
        <form className="bidblock__form" onSubmit={submitBid}>
          {/* Sits directly under "Place your bid below" (team request). */}
          <p className="bidblock__help">
            Enter <strong>{dollars(minNextDollars * 100)}</strong> or more. The highest bid wins!
          </p>
          <div className="bidblock__form-row">
            <div className="bidblock__field">
              <span className="bidblock__currency" aria-hidden="true">$</span>
              {/* type="text" + manual formatting: no native spinners, and the
                  value reads as money ("6,000.00") instead of a bare number. */}
              <input
                ref={bidInputRef}
                type="text"
                inputMode="numeric"
                placeholder={`${minNextDollars.toLocaleString('en-US')}.00`}
                value={bidDisplay}
                onChange={(e) => setMaxBid(e.target.value.replace(/\D/g, '').slice(0, 7))}
                onFocus={() => setBidFocused(true)}
                onBlur={() => setBidFocused(false)}
                className="bidblock__input"
                aria-label="Your bid in US dollars"
              />
            </div>
            <button
              type="submit"
              className="bidblock__submit"
              disabled={!maxBid || parseInt(maxBid, 10) < minNextDollars}
            >
              Place bid
            </button>
          </div>
          <p className="bidblock__help">Plus shipping</p>
        </form>

        {buyNowAvailable && !confirmingBuyNow && (
          <div className="bidblock__buynow">
            <div className="bidblock__or" aria-hidden="true">or</div>
            <button
              type="button"
              className="bidblock__buynow-btn"
              onClick={() => setConfirmingBuyNow(true)}
            >
              Buy now · {dollars(auction.buy_now_price!)}
            </button>
            <p className="bidblock__buynote">
              Buy now ends the auction immediately. Available until the first bid is placed.
            </p>
          </div>
        )}
        {buyNowAvailable && confirmingBuyNow && (
          <div className="bidblock__buyconfirm">
            <p className="bidblock__buyconfirm-price">
              Buy now for <strong>{dollars(auction.buy_now_price!)}</strong>
              {postage !== null && postage > 0 && (
                <> + {dollars(postage)} postage = <strong>{dollars(auction.buy_now_price! + postage)}</strong> total</>
              )}
            </p>
            <p className="bidblock__buynote">
              The auction ends immediately and the lot is yours; your card on file is charged once the team confirms.
            </p>
            <div className="bidblock__buyconfirm-actions">
              <button
                type="button"
                className="bidblock__submit"
                disabled={buyNowBusy}
                onClick={handleConfirmBuyNow}
              >
                {buyNowBusy ? 'Buying…' : 'Confirm buy now'}
              </button>
              <button
                type="button"
                className="bidblock__cancel"
                disabled={buyNowBusy}
                onClick={() => setConfirmingBuyNow(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        </>
      )}

      {bidError && (
        <p className="bidblock__error" role="alert" onClick={onDismissBidError}>
          {bidError}
        </p>
      )}
      </div>
      )}
      </>
      )}
    </section>
    {/* Postage maths live OUTSIDE the bid card (team request) so the card
        stays focused on the bid itself. */}
    {!isEnded && isLive && canBid && !isAuthor && cardOnFile === true &&
      postage !== null && postage > 0 && (
      <p className="bidblock__receipt bidblock__receipt--outside">
        Your bid <b>{dollars(receiptBidDollars * 100)}</b>
        {' '}+ postage <b>{dollars(postage)}</b>
        {' '}= <b>{dollars(receiptBidDollars * 100 + postage)}</b> total
      </p>
    )}
    </div>
    {/* Member bidding shortcut only: viewers who can't bid (author,
        non-members, no card on file) get no sticky CTA — jumping them to a
        card with no bid form would be a dead end. */}
    {isLive && canBid && !isAuthor && cardOnFile === true && !stackInView && (
      <StickyBidBar auction={auction} onJump={jumpToBidCard} />
    )}
    </>
  );
}

/** Mobile-only sticky bid bar (CSS hides it above 880px): pinned to the
 *  bottom while a live lot's bid card is scrolled off-screen. Shows the
 *  current/starting amount + a compact ticking clock; the pill scrolls back
 *  to the bid card and focuses the input. */
function StickyBidBar({ auction, onJump }: { auction: PostAuction; onJump: () => void }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, new Date(auction.ends_at).getTime() - now);
  const totalSec = Math.floor(remaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock = `${pad(Math.floor((totalSec % 86400) / 3600))}:${pad(Math.floor((totalSec % 3600) / 60))}:${pad(totalSec % 60)}`;
  const urgent = remaining < 60 * 60 * 1000;
  const amount = auction.bid_count > 0 ? auction.current_high_amount : auction.min_bid;
  return (
    <div className="bidbar" role="region" aria-label="Live bidding shortcut">
      <div className="bidbar__info">
        <span className="bidbar__price">{dollars(amount)}</span>
        <span className={`bidbar__time ${urgent ? 'bidbar__time--urgent' : ''}`}>
          Ends {days > 0 ? `${days}d ` : ''}{clock}
        </span>
      </div>
      <button type="button" className="bidbar__cta" onClick={onJump}>Place bid</button>
    </div>
  );
}

/** State pill text for the lot header ("LIVE" / "OPENS in 6h" / "ENDED"). */
function auctionStatePill(a: PostAuction): string {
  if (a.state === 'live') return 'LIVE';
  if (a.state === 'scheduled') return `OPENS ${relativeTime(a.starts_at)}`;
  return 'ENDED';
}

/** The "soft details" half of a lot page: gallery on the left, name +
 *  description + Material/Size/Shipping dropdowns on the right. The bidding
 *  block (AuctionBlock) renders separately below it. */
function AuctionDetail({
  post,
  auction,
  gallery,
}: {
  post: PostType;
  auction: PostAuction;
  gallery: React.ReactNode;
}) {
  const heading = (!post.hide_title && post.title) || auction.lot_name || post.title;
  const hasDetails = !!(auction.material_info || auction.size_info || auction.shipping_info);
  return (
    <div className="lot">
      <div className="lot__gallery">{gallery}</div>
      <div className="lot__info">
        <div className="lot__tags">
          <span className={`lot__state lot__state--${auction.state}`}>{auctionStatePill(auction)}</span>
          <span className="lot__num">LOT {auction.lot_number}</span>
        </div>
        <h1 className="lot__name">{heading}</h1>
        {post.body_md && <RichTextView html={post.body_md} className="lot__desc" />}
        {hasDetails && (
          <div className="lot__disclosures">
            {auction.material_info && (
              <Disclosure title="Material">
                <p className="lot__detail-text">{auction.material_info}</p>
              </Disclosure>
            )}
            {auction.size_info && (
              <Disclosure title="Size details">
                <p className="lot__detail-text">{auction.size_info}</p>
              </Disclosure>
            )}
            {auction.shipping_info && (
              <Disclosure title="Shipping">
                <p className="lot__detail-text">{auction.shipping_info}</p>
              </Disclosure>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Live ticking countdown — the black chip under the price. Counts down to
 *  starts_at while scheduled, to ends_at while live. Never rendered once the
 *  lot ends (the card collapses to the SOLD/UNSOLD stamp), so the ended
 *  guard just returns nothing. */
function AuctionCountdown({ auction }: { auction: PostAuction }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (auction.state === 'ended') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [auction.state]);

  if (auction.state === 'ended') return null;

  const target = auction.state === 'scheduled' ? auction.starts_at : auction.ends_at;
  const remaining = Math.max(0, new Date(target).getTime() - now);
  const totalSec = Math.floor(remaining / 1000);
  // "4 days 10:30:45" — the days figure splits out in words, the rest
  // stays a HH:MM:SS clock.
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const hms = [hours, mins, secs].map((v) => String(v).padStart(2, '0')).join(':');
  const clock = days > 0 ? `${days} ${days === 1 ? 'day' : 'days'} ${hms}` : hms;
  const label = auction.state === 'scheduled' ? 'Opens in' : 'Ends in';
  // Under an hour on a live lot — flag it so the clock can pulse / go red.
  const urgent = auction.state === 'live' && remaining < 60 * 60 * 1000;

  return (
    <div className={`countdown ${urgent ? 'countdown--urgent' : ''}`}>
      <span className="countdown__label">{label}</span>
      <span className="countdown__clock" role="timer">{clock}</span>
    </div>
  );
}

/** Multi-media post body: the swipe carousel plus the full-screen lightbox it
 *  opens. Local state lives here so the parent's hook order stays stable. */
function PostMedia({ items, aspect, thumbs }: { items: Media[]; aspect: number; thumbs?: boolean }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  return (
    <>
      <div className="post__media">
        <MediaCarousel
          items={items}
          aspect={aspect}
          variant={thumbs ? 'thumbs' : 'dots'}
          onOpenLightbox={(i) => setLightboxIndex(i)}
        />
      </div>
      {lightboxIndex !== null && (
        <Lightbox
          items={items}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </>
  );
}

function LikeButton({ n, liked, onToggle }: { n: number; liked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={`like ${liked ? 'like--on' : ''}`}
      onClick={onToggle}
      aria-pressed={liked}
      aria-label="Like this post"
    >
      <span aria-hidden="true">{liked ? '♥' : '♡'}</span>
      <span>{n}</span>
    </button>
  );
}

function ShareButton({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  async function share() {
    const url = `${window.location.origin}/p/${slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ url, title: 'CONVERTED CLOSET' });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }
    } catch { /* cancelled */ }
  }
  return (
    <button type="button" className="share" onClick={share} aria-label="Share this post">
      {copied ? 'Link copied' : 'Share'}
    </button>
  );
}

function ShareCommentButton({ postSlug, commentId }: { postSlug: string; commentId: string }) {
  const [copied, setCopied] = useState(false);
  async function share() {
    const url = `${window.location.origin}/p/${postSlug}#comment=${commentId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  }
  return (
    <button
      type="button"
      className="comment__action comment__action--share"
      onClick={share}
      aria-label="Copy link to this comment"
    >
      {copied ? 'Copied' : 'Share'}
    </button>
  );
}

function dollars(cents: number): string {
  // Always two decimals ("$40.00") — prices read as money, and the receipt
  // line is exactly what the card is charged.
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Auction-flavoured relative time. Short countdown for events within 24h
 * ("in 1h 23m" / "12m ago"), falls back to "Fri 6:00 PM" for longer
 * horizons. Used by AuctionBlock's state pill + bid history.
 */
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMs = t - Date.now();
  const absMs = Math.abs(diffMs);
  const future = diffMs > 0;

  if (absMs < 24 * 60 * 60 * 1000) {
    const totalMin = Math.floor(absMs / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const stamp = h > 0 ? `${h}h ${m}m` : `${m}m`;
    return future ? `in ${stamp}` : `${stamp} ago`;
  }
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

/**
 * Map raw RPC exception strings from place_bid / buy_now into member-facing
 * copy. The Postgres exceptions are stable (see migration 031 — the `raise
 * exception '<name>'` calls) so a substring match is robust enough.
 */
function mapBidError(err: any): string {
  const msg = String(err?.message ?? '');
  if (msg.includes('auction_not_started')) return "Bidding hasn't opened yet.";
  if (msg.includes('auction_ended'))       return 'Bidding has closed.';
  if (msg.includes('buy_now_unavailable')) return 'Buy now is no longer available on this lot.';
  if (msg.includes('no_buy_now'))          return 'Buy now is no longer available on this lot.';
  if (msg.includes('bid_too_low'))         return 'Your bid must beat the current bid by at least $1.';
  if (msg.includes('below_min_bid'))       return "Your bid is below this lot's minimum.";
  if (msg.includes('self_bid'))            return "You can't bid on your own lot.";
  if (msg.includes('no_card_on_file'))     return 'Add a payment card before bidding.';
  if (msg.includes('no_access'))           return 'Members only. Sign in to bid.';
  if (msg.includes('not_authenticated'))   return 'Sign in to bid.';
  return 'Something broke placing that bid. Try again.';
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
